import React, { useState, useRef, useCallback } from "react";
import { Box, Typography } from "@mui/material";
import useTheme from "../../../../common/hooks/useTheme";

const UploadIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="24"
    height="24"
    viewBox="0 0 24 24"
    fill="none"
  >
    <path
      d="M21 15C20.6022 15 20.2206 15.158 19.9393 15.4393C19.658 15.7206 19.5 16.1022 19.5 16.5V19.5H4.5V16.5C4.5 16.1022 4.34196 15.7206 4.06066 15.4393C3.77936 15.158 3.39782 15 3 15C2.60218 15 2.22064 15.158 1.93934 15.4393C1.65804 15.7206 1.5 16.1022 1.5 16.5V19.5C1.5 20.2956 1.81607 21.0587 2.37868 21.6213C2.94129 22.1839 3.70435 22.5 4.5 22.5H19.5C20.2956 22.5 21.0587 22.1839 21.6213 21.6213C22.1839 21.0587 22.5 20.2956 22.5 19.5V16.5C22.5 16.1022 22.342 15.7206 22.0607 15.4393C21.7794 15.158 21.3978 15 21 15Z"
      fill="#6D7075"
    />
    <path
      d="M7.06062 10.0602L10.5001 6.62068V14.9997C10.5001 15.3975 10.6582 15.779 10.9395 16.0603C11.2208 16.3416 11.6023 16.4997 12.0001 16.4997C12.3979 16.4997 12.7795 16.3416 13.0608 16.0603C13.3421 15.779 13.5001 15.3975 13.5001 14.9997V6.62068L16.9396 10.0602C17.2225 10.3334 17.6014 10.4846 17.9947 10.4812C18.388 10.4778 18.7642 10.32 19.0423 10.0419C19.3205 9.76379 19.4782 9.38758 19.4816 8.99428C19.485 8.60099 19.3339 8.22208 19.0606 7.93918L13.0606 1.93918C12.7793 1.65797 12.3979 1.5 12.0001 1.5C11.6024 1.5 11.2209 1.65797 10.9396 1.93918L4.93962 7.93918C4.66639 8.22208 4.51519 8.60099 4.51861 8.99428C4.52203 9.38758 4.67978 9.76379 4.95789 10.0419C5.23601 10.32 5.61223 10.4778 6.00552 10.4812C6.39882 10.4846 6.77772 10.3334 7.06062 10.0602Z"
      fill="#6D7075"
    />
  </svg>
);

interface ImageUploadProps {
  uploadedImage: string | null;
  setUploadedImage: (uploadedImage: string | null) => void;
  onFileSelect?: (file: File | null) => void;
}

const ImageUpload: React.FC<ImageUploadProps> = ({
  uploadedImage,
  setUploadedImage,
  onFileSelect,
}) => {
  const [dragOver, setDragOver] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const theme = useTheme();

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(false);
  }, []);

  const validateImageType = useCallback((file: File): boolean => {
    const validTypes = ["image/jpeg", "image/png"];
    return validTypes.includes(file.type);
  }, []);

  const handleFileUpload = useCallback(
    (file: File) => {
      if (!validateImageType(file)) {
        setError("Please upload a JPEG or PNG image file");
        return;
      }

      setError(null);
      const reader = new FileReader();
      reader.onload = (e) => {
        if (e.target?.result) {
          setUploadedImage(e.target.result as string);
          onFileSelect?.(file);
        }
      };
      reader.readAsDataURL(file);
    },
    [setUploadedImage, onFileSelect, validateImageType]
  );

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      setDragOver(false);

      const files = e.dataTransfer.files;
      if (files.length > 0) {
        const file = files[0];
        if (file.type.startsWith("image/")) {
          handleFileUpload(file);
        }
      }
    },
    [setDragOver, handleFileUpload]
  );

  const handleClick = () => {
    fileInputRef.current?.click();
  };

  const handleFileSelect = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const files = e.target.files;
      if (files && files.length > 0) {
        const file = files[0];
        if (file.type.startsWith("image/")) {
          handleFileUpload(file);
        }
      }
    },
    [handleFileUpload]
  );

  return (
    <Box
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
      onClick={handleClick}
      sx={{
        width: "100%",
        height: "160px",
        border: `2px dashed ${theme.colors_ds.NEUTRAL_BORDER_WEAK}`,
        borderRadius: 2,
        textAlign: "center",
        cursor: "pointer",
        transition: "all 0.3s ease-in-out",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        position: "relative",
        "&:hover": {
          borderColor: theme.colors_ds.PRIMARY_ICON_MAIN,
          backgroundColor: theme.colors_ds.NEUTRAL_BACKGROUND_WEAK,
        },
        ...(dragOver && {
          borderColor: theme.colors_ds.PRIMARY_ICON_MAIN,
          backgroundColor: theme.colors_ds.NEUTRAL_BACKGROUND_WEAK,
        }),
      }}
    >
      <input
        ref={fileInputRef}
        type="file"
        accept="image/jpeg,image/png"
        style={{ display: "none" }}
        onChange={handleFileSelect}
      />

      {uploadedImage ? (
        <Box
          sx={{
            width: "100%",
            overflow: "hidden",
            borderRadius: "8px",
          }}
        >
          <Box
            component="img"
            src={uploadedImage}
            alt={"Uploaded Image"}
            sx={{
              width: "100%",
            }}
          />
        </Box>
      ) : (
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            height: "100%",
          }}
        >
          <UploadIcon />
          <Typography
            sx={{
              mt: 1,
              fontFamily: "var(--font-Medium)",
              fontSize: "14px",
              color: theme.colors_ds.NEUTRAL_TEXT_WEAK,
            }}
          >
            Drag & Drop or click to select files
          </Typography>
        </Box>
      )}
      {error && (
        <Typography
          sx={{
            mt: 1,
            fontFamily: "var(--font-Medium)",
            fontSize: "10px",
            color: theme.colors_ds.DANGER_GRAPH_DEFICIT,
            position: "absolute",
            bottom: "-20px",
            left: 0,
          }}
        >
          {error}
        </Typography>
      )}
    </Box>
  );
};

export default ImageUpload;
